'use client';

import { useEffect, useState, memo } from 'react';
import dynamic from 'next/dynamic';
import styles from './ExpressCheckout.module.scss';
import ApplePayEnabled from '@next-app/components/shared/ApplePayButton/ApplepayEnabled';
import GooglePayEnabled from '@next-app/components/shared/GooglePayButton/GooglePayEnabled';
import Conditional from '@next-app/components/Conditional';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllProfileAddress,
  selectGooglePaymentRequest,
  selectHasItemFromRegistry,
  selectHasItemFromShippingList,
  selectIsBopisOrder,
  selectIsCheckoutDetailsLoaded,
  selectIsGooglePayAuthorized,
  selectIsInternationalOrder,
  selectIsPayPalEnabled,
  selectIsPayPalPaymentMethodAuthorized,
  selectIsPureDigitalOrder,
  selectShowRegistryAddressModal,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import SkeletonUI from '@next-app/components/Skeleton/Skeleton';
import {
  selectDynSess,
  selectFeatureToggles,
  selectIsFurniture,
} from '@next-app/lib/features/InitSelectors';
import { fetchCheckoutDetails } from '@next-app/utils/CheckoutHelper';
import { AddressInfo } from '@next-app/lib/features/checkout/Checkout.interface';
import {
  isNotEmptyObject,
  isNotNullOrUndefinedorEmpty,
} from '@next-app/utils/common-utils';
import Loader from '@next-app/components/shared/Loader/Loader';
import { googlePay } from '@next-app/constants/constants';

const PayPalButton = dynamic(
  () => import('@next-app/components/PDP/AddedToCartModal/PayPalButton'),
  { ssr: true, loading: () => <SkeletonUI width={'100%'} height={20} /> },
);

const ApplePayButton = dynamic(
  () => import('@next-app/components/shared/ApplePayButton/ApplePayButton'),
  { ssr: true, loading: () => <SkeletonUI width={'100%'} height={20} /> },
);
const GooglePayButton = dynamic(
  () => import('@next-app/components/shared/GooglePayButton/GooglePayButton'),
  { ssr: true, loading: () => <SkeletonUI width={'100%'} height={20} /> },
);

// should lazy load Paypal, Google Pay and Apply pay (whichever is applicable based on feature flags)
const ExpressCheckout = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const [paymentSolutionErrorMessage, setPaymentSolutionErrorMessage] =
    useState('');
  const [showRegistryModal, setShowRegistryModal] = useState(false);
  const isInternationalOrder = useSelector(selectIsInternationalOrder) || false;
  const googlePayPaymentRequest = useSelector(selectGooglePaymentRequest) || {};
  const clearTheListOrder = useSelector(selectHasItemFromShippingList) || false;
  const registryOrder = useSelector(selectHasItemFromRegistry) || false;
  const bopisOrder = useSelector(selectIsBopisOrder) || false;
  const payPalEnabled = useSelector(selectIsPayPalEnabled) || false;
  const isCheckoutDetailsLoaded =
    useSelector(selectIsCheckoutDetailsLoaded) || false;
  const dynSessConf = useSelector(selectDynSess);
  const { allAddresses, addressId } = useSelector(selectAllProfileAddress);
  const isPureDigitalOrder = useSelector(selectIsPureDigitalOrder);
  const showRegistryAddressModal = useSelector(selectShowRegistryAddressModal);
  const [loading, setLoading] = useState<boolean>(false);
  const isFurniture = useSelector(selectIsFurniture);
  const featureToggle = useSelector(selectFeatureToggles);
  const { applePayEnabled = false, googlePayEnabled = false } =
    featureToggle || {};

  const openRegistryModal = () => {
    setShowRegistryModal(true);
  };

  const handleSuccessPayment = async (): Promise<void> => {
    setLoading(true);
    try {
      await fetchCheckoutDetails({ dynSessConf, dispatch, isFurniture });
    } catch (error) {
      console.error('Failed to fetch checkout details after payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPayPalAuthorized = useSelector(selectIsPayPalPaymentMethodAuthorized);
  const isGooglePayAuthorized = useSelector(selectIsGooglePayAuthorized);
  const [isRegistryAddressModal, setIsRegistryOrderModal] =
    useState(registryOrder);
  const [isCTLAddressModal, setIsCTLAddressModal] = useState(false);

  // Check if order type is digital or store pickup only
  function isDigitalOrStorePickupOnly() {
    if (
      googlePayPaymentRequest !== undefined &&
      googlePayPaymentRequest !== null &&
      isNotEmptyObject(googlePayPaymentRequest)
    ) {
      return (
        googlePayPaymentRequest?.orderShipType ===
          googlePay.IN_STORE_PICKUP_ONLY ||
        googlePayPaymentRequest?.orderShipType ===
          googlePay.EMAIL_AND_IN_STORE_PICKUP ||
        googlePayPaymentRequest?.orderShipType === googlePay.EMAIL_ONLY
      );
    }
  }

  useEffect(() => {
    const selectedDeliveryAddr = allAddresses.find(
      (address: AddressInfo) => address.repositoryId === addressId,
    );
    if (
      isNotNullOrUndefinedorEmpty(selectedDeliveryAddr?.displayName) &&
      selectedDeliveryAddr?.displayName.includes('list owner')
    ) {
      setIsRegistryOrderModal(false);
      setIsCTLAddressModal(true);
    } else if (
      isNotNullOrUndefinedorEmpty(selectedDeliveryAddr?.displayName) &&
      selectedDeliveryAddr?.displayName.includes('Registry Address')
    ) {
      setIsRegistryOrderModal(true);
      setIsCTLAddressModal(false);
    } else {
      setIsRegistryOrderModal(false);
      setIsCTLAddressModal(false);
    }
  }, [allAddresses, addressId]);

  return (
    <>
      <Conditional
        if={
          !isPayPalAuthorized &&
          !isGooglePayAuthorized &&
          !isInternationalOrder &&
          (payPalEnabled || applePayEnabled || googlePayEnabled)
        }
      >
        <section className="mb-md-ll-5">
          <div className="d-flex justify-content-center mb-3 fs-base lh-base">
            Express Checkout
          </div>
          <div
            className={`d-flex gap-ll-3 m-0 mb-ll-5 ${styles['payment-btn-wrapper']}`}
          >
            <Conditional if={!isCheckoutDetailsLoaded}>
              <SkeletonUI width={'100%'} height={20} />
            </Conditional>
            <Conditional if={isCheckoutDetailsLoaded === true}>
              <>
                {/* PayPal */}
                <Conditional if={!bopisOrder && payPalEnabled}>
                  <div className="col mt-0">
                    <PayPalButton
                      isCheckout={true}
                      openRegistryModal={openRegistryModal}
                      showRegistryAddressModal={isRegistryAddressModal}
                      showClearTheListAddressModal={isCTLAddressModal}
                      showCustomlogo={false}
                      height={44}
                      buttonColor={`gold`}
                      customClass={`${styles['paypal-button-wrapper']}`}
                      handleSuccessPayment={handleSuccessPayment}
                      isFurniture={isFurniture}
                      useRegistryAddress={
                        isPureDigitalOrder &&
                        registryOrder &&
                        !showRegistryAddressModal
                      }
                      isDigitalOrStorePickupOnly={isDigitalOrStorePickupOnly()}
                    />
                  </div>
                </Conditional>

                {/* Apple Pay */}
                <ApplePayEnabled showApplePay={true} isCheckoutPage={true}>
                  <ApplePayButton
                    // update show registry modal based on load checkout details api response
                    showRegistryAddressModal={registryOrder}
                    paymentSolutionErrorMessage={paymentSolutionErrorMessage}
                    setPaymentSolutionErrorMessage={
                      setPaymentSolutionErrorMessage
                    }
                    clearTheListOrder={clearTheListOrder}
                    isCheckoutPage={true}
                    radius={!isFurniture ? true : false}
                    customClass={`${styles['applepay-button-wrapper']}`}
                    isDigitalOrStorePickupOnly={isDigitalOrStorePickupOnly()}
                  />
                </ApplePayEnabled>
                {/* Google Pay */}
                <Conditional
                  if={
                    googlePayPaymentRequest &&
                    Object.keys(googlePayPaymentRequest).length
                  }
                >
                  <GooglePayEnabled>
                    <div className="col mt-0">
                      <GooglePayButton
                        googlePayPaymentRequest={googlePayPaymentRequest}
                        showRegistryAddressModal={registryOrder}
                        clearTheListOrder={clearTheListOrder}
                        isCheckoutPage={true}
                        radius={!isFurniture ? true : false}
                        customClass={`${styles['gPay-btn-wrapper']}`}
                        handleSuccessPayment={handleSuccessPayment}
                        isDigitalOrStorePickupOnly={isDigitalOrStorePickupOnly()}
                      />
                    </div>
                  </GooglePayEnabled>
                </Conditional>
              </>
            </Conditional>
          </div>
          <div
            className={`light mt-2 text-center position-relative ${styles['orSection']}`}
          >
            <span className="position-relative px-ll-3">OR</span>
          </div>
        </section>
      </Conditional>
      {/* Loading Indicator */}
      <Conditional if={loading}>
        <Loader aria-label="Processing your order" />
      </Conditional>
    </>
  );
};
export default memo(ExpressCheckout);
